import type {
  AssignmentsInput,
  BrandAccount,
  BrandAccountInputTho,
  ChannelGroup,
  ChannelProfile,
  ChannelProfileInput,
  ChannelRow,
  ChannelSummary,
  ChannelsSummary,
  CreateChannelInput,
  KetQuaLuuTru,
  ListChannelsResponse,
  MonetizationDetail,
  MonetizationInput,
  NguoiPhuTrach,
  UpdateChannelInput,
  XemTruocXoaKenh,
  XoaKenhInput,
} from '@qlk/shared';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';

import { api, goiApi, type LoiApi } from '../../lib/api.js';
import { KHOA_MOC_SO_LIEU } from '../analytics/api.js';

const GOC = '/api/v1/channels';

export const KHOA = {
  danhSach: (loc: BoLocKenh) => ['channels', 'list', loc] as const,
  tomTat: (period: string) => ['channels', 'summary', period] as const,
  kenh: (id: string) => ['channels', 'detail', id] as const,
  hoSo: (id: string) => ['channels', 'profile', id] as const,
  thuongHieu: (id: string) => ['channels', 'brand-account', id] as const,
  nhom: ['channels', 'groups'] as const,
  nguoiDung: ['channels', 'users'] as const,
  xemTruocXoa: (id: string) => ['channels', 'xem-truoc-xoa', id] as const,
};

export interface BoLocKenh {
  q?: string;
  tab?: string;
  sort?: string;
  /** **Mã kỳ** của ô chọn chung (`@qlk/shared/ky.ts`), không chỉ năm preset cũ. */
  period?: string;
  paymentPartnerId?: string;
  groupId?: string;
  market?: string;
  ownerId?: string;
  /** Tìm kênh mà email này còn quyền trên Tài khoản thương hiệu. */
  emailQuyen?: string;
  /** Lọc theo tình trạng kiếm tiền (YPP) đang khai. */
  monetizationStatus?: string;
}

function thamSo(loc: BoLocKenh, cursor?: string): string {
  const sp = new URLSearchParams();
  for (const [khoa, giaTri] of Object.entries(loc)) {
    if (giaTri) sp.set(khoa, String(giaTri));
  }
  if (cursor) sp.set('cursor', cursor);
  const chuoi = sp.toString();
  return chuoi ? `?${chuoi}` : '';
}

/** Danh sách kênh, phân trang theo cursor bằng nút "Tải thêm". */
export function useDanhSachKenh(loc: BoLocKenh) {
  return useInfiniteQuery<
    ListChannelsResponse,
    LoiApi,
    InfiniteData<ListChannelsResponse>,
    ReturnType<typeof KHOA.danhSach>,
    string | undefined
  >({
    queryKey: KHOA.danhSach(loc),
    queryFn: ({ pageParam }) => api.get<ListChannelsResponse>(`${GOC}${thamSo(loc, pageParam)}`),
    initialPageParam: undefined,
    getNextPageParam: (trang) => trang.nextCursor ?? undefined,
  });
}

export function useTomTatKenh(period: string) {
  return useQuery<ChannelsSummary, LoiApi>({
    queryKey: KHOA.tomTat(period),
    queryFn: () => api.get<ChannelsSummary>(`${GOC}/summary?ky=${encodeURIComponent(period)}`),
  });
}

export function useKenh(id: string | undefined) {
  return useQuery<ChannelRow, LoiApi>({
    queryKey: KHOA.kenh(id ?? ''),
    queryFn: () => api.get<ChannelRow>(`${GOC}/${id ?? ''}`),
    enabled: Boolean(id),
  });
}

export function useNhomKenh() {
  return useQuery<ChannelGroup[], LoiApi>({
    queryKey: KHOA.nhom,
    queryFn: () => api.get<ChannelGroup[]>(`${GOC}/nhom`),
  });
}

export function useNguoiPhuTrach(batDau = true) {
  return useQuery<NguoiPhuTrach[], LoiApi>({
    queryKey: KHOA.nguoiDung,
    queryFn: () => api.get<NguoiPhuTrach[]>(`${GOC}/nguoi-dung`),
    enabled: batDau,
  });
}

export function useHoSoKenh(id: string | undefined) {
  return useQuery<ChannelProfile, LoiApi>({
    queryKey: KHOA.hoSo(id ?? ''),
    queryFn: () => api.get<ChannelProfile>(`${GOC}/${id ?? ''}/ho-so`),
    enabled: Boolean(id),
  });
}

/* ------------------------------- Mutation ------------------------------ */

function lamMoiDanhSach(qc: ReturnType<typeof useQueryClient>) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: ['channels', 'list'] }),
    qc.invalidateQueries({ queryKey: ['channels', 'summary'] }),
    /* Thêm / lưu trữ / khôi phục kênh đổi **phạm vi** của mốc dữ liệu, nên
       danh sách năm trong ô chọn kỳ phải tính lại. */
    qc.invalidateQueries({ queryKey: KHOA_MOC_SO_LIEU }),
  ]);
}

export function useTaoKenh() {
  const qc = useQueryClient();
  return useMutation<ChannelSummary, LoiApi, CreateChannelInput>({
    mutationFn: (input) => api.post<ChannelSummary>(GOC, input),
    onSuccess: () => lamMoiDanhSach(qc),
  });
}

export function useCapNhatKenh(id: string) {
  const qc = useQueryClient();
  return useMutation<ChannelSummary, LoiApi, UpdateChannelInput>({
    mutationFn: (input) =>
      goiApi<ChannelSummary>(`${GOC}/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KHOA.kenh(id) });
      await lamMoiDanhSach(qc);
    },
  });
}

/**
 * Tình trạng kiếm tiền (YPP) kèm lịch sử đổi.
 *
 * Dữ liệu **nhập tay**: YouTube không có API trả tình trạng YPP cho chủ kênh.
 */
export function useTinhTrangKiemTien(id: string | undefined) {
  return useQuery<MonetizationDetail, LoiApi>({
    queryKey: ['channels', 'kiem-tien', id ?? ''],
    queryFn: () => api.get<MonetizationDetail>(`${GOC}/${id ?? ''}/kiem-tien`),
    enabled: Boolean(id),
  });
}

/** Lưu tình trạng kiếm tiền. Chỉ `channels:admin` gọi được (máy chủ kiểm). */
export function useLuuKiemTien(id: string) {
  const qc = useQueryClient();
  return useMutation<MonetizationDetail, LoiApi, MonetizationInput>({
    mutationFn: (input) =>
      goiApi<MonetizationDetail>(`${GOC}/${id}/kiem-tien`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: async (du) => {
      qc.setQueryData(['channels', 'kiem-tien', id], du);
      // Huy hiệu ở danh sách và ô "N kênh đang kiếm tiền" đổi theo.
      await lamMoiDanhSach(qc);
    },
  });
}

export function useLuuHoSoKenh(id: string) {
  const qc = useQueryClient();
  return useMutation<ChannelProfile, LoiApi, ChannelProfileInput>({
    mutationFn: (input) =>
      goiApi<ChannelProfile>(`${GOC}/${id}/ho-so`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: (du) => qc.setQueryData(KHOA.hoSo(id), du),
  });
}

export function useLuuPhanCong(id: string) {
  const qc = useQueryClient();
  return useMutation<{ assignees: ChannelRow['assignees'] }, LoiApi, AssignmentsInput>({
    mutationFn: (input) =>
      goiApi<{ assignees: ChannelRow['assignees'] }>(`${GOC}/${id}/phan-cong`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KHOA.kenh(id) });
      await lamMoiDanhSach(qc);
    },
  });
}

/**
 * Lưu trữ kênh. Phản hồi kèm việc các module khác đã làm (thu hồi token, huỷ
 * bài lịch chưa upload) và việc không làm được, để giao diện báo lại.
 */
export function useLuuTruKenh(id: string) {
  const qc = useQueryClient();
  return useMutation<KetQuaLuuTru, LoiApi, void>({
    mutationFn: () => api.post<KetQuaLuuTru>(`${GOC}/${id}/luu-tru`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KHOA.kenh(id) });
      await lamMoiDanhSach(qc);
    },
  });
}

export function useKhoiPhucKenh(id: string) {
  const qc = useQueryClient();
  return useMutation<ChannelSummary, LoiApi, void>({
    mutationFn: () => api.post<ChannelSummary>(`${GOC}/${id}/khoi-phuc`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: KHOA.kenh(id) });
      await lamMoiDanhSach(qc);
    },
  });
}

/**
 * Đếm trước những gì mất khi xoá vĩnh viễn. Chỉ gọi khi hộp thoại mở (`bat`)
 * để người không mở hộp thoại không tốn một lượt gọi admin.
 */
export function useXemTruocXoaKenh(id: string, bat: boolean) {
  return useQuery<XemTruocXoaKenh, LoiApi>({
    queryKey: KHOA.xemTruocXoa(id),
    queryFn: () => api.get<XemTruocXoaKenh>(`${GOC}/${id}/xem-truoc-xoa`),
    enabled: bat && id !== '',
    // Số dòng đổi theo dữ liệu thật, đừng giữ lâu trong cache.
    staleTime: 0,
  });
}

/** Xoá vĩnh viễn. Phải gõ lại đúng tên kênh, máy chủ kiểm lại. */
export function useXoaKenh(id: string) {
  const qc = useQueryClient();
  return useMutation<{ soDong: Record<string, number> }, LoiApi, XoaKenhInput>({
    mutationFn: (input) =>
      goiApi<{ soDong: Record<string, number> }>(`${GOC}/${id}`, {
        method: 'DELETE',
        body: JSON.stringify(input),
      }),
    onSuccess: async () => {
      qc.removeQueries({ queryKey: KHOA.kenh(id) });
      await lamMoiDanhSach(qc);
    },
  });
}

/** Đường dẫn tải Excel dữ liệu kênh. Trả về file nên để thẻ `a` tự tải. */
export function duongDanXuatExcelKenh(id: string): string {
  return `${GOC}/${id}/xuat-excel`;
}

/* --------------------- Tài khoản thương hiệu --------------------- */

export function useThuongHieuKenh(id: string | undefined) {
  return useQuery<BrandAccount, LoiApi>({
    queryKey: KHOA.thuongHieu(id ?? ''),
    queryFn: () => api.get<BrandAccount>(`${GOC}/${id ?? ''}/thuong-hieu`),
    enabled: Boolean(id),
  });
}

export function useLuuThuongHieuKenh(id: string) {
  const qc = useQueryClient();
  return useMutation<BrandAccount, LoiApi, BrandAccountInputTho>({
    mutationFn: (input) =>
      goiApi<BrandAccount>(`${GOC}/${id}/thuong-hieu`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: async (du) => {
      qc.setQueryData(KHOA.thuongHieu(id), du);
      // Danh sách kênh lọc theo email nên phải làm mới theo.
      await lamMoiDanhSach(qc);
    },
  });
}

export function useDanhDauDaKiemTraThuongHieu(id: string) {
  const qc = useQueryClient();
  return useMutation<BrandAccount, LoiApi, void>({
    mutationFn: () => api.post<BrandAccount>(`${GOC}/${id}/thuong-hieu/da-kiem-tra`),
    onSuccess: (du) => qc.setQueryData(KHOA.thuongHieu(id), du),
  });
}

/** Lấy tên và handle phía YouTube làm tên chính thức trong app. */
export function useDongBoTenYoutube(id: string) {
  const qc = useQueryClient();
  return useMutation<{ name: string; handle: string }, LoiApi, void>({
    mutationFn: () =>
      api.post<{ name: string; handle: string }>(`${GOC}/${id}/dong-bo-ten-youtube`),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: KHOA.thuongHieu(id) }),
        qc.invalidateQueries({ queryKey: KHOA.kenh(id) }),
        lamMoiDanhSach(qc),
      ]);
    },
  });
}
